import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, CheckCircle, XCircle, ExternalLink, Filter, 
  RefreshCw, ChevronDown, ChevronUp, Loader2, AlertCircle,
  Send, X, MessageSquare, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";


import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const SyncRequestsPage = () => {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');


  const [expandedRequest, setExpandedRequest] = useState(null);
  
  // Complete dialog state
  const [completeDialog, setCompleteDialog] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (locationFilter && locationFilter !== 'all') {
        params.append('location_id', locationFilter);
      }
      if (dateFrom) {
        params.append('start_date', `${dateFrom}T00:00:00.000Z`);
      }
      if (dateTo) {
        params.append('end_date', `${dateTo}T23:59:59.999Z`);
      }
      
      const response = await axios.get(`${API}/sync-requests?${params.toString()}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch sync requests:', error);
      toast.error('Failed to load sync requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, locationFilter, dateFrom, dateTo]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync-requests/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStats();
    fetchLocations();
  }, [fetchRequests, fetchStats, fetchLocations]);

  const handleMarkComplete = async () => {
    if (!completeDialog) return;
    setCompleting(true);
    
    try {
      await axios.patch(`${API}/sync-requests/${completeDialog.id}/complete`, {
        notes: completionNotes
      });
      
      toast.success('Request marked as complete. User has been notified.');
      setCompleteDialog(null);
      setCompletionNotes('');
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete request');
    } finally {
      setCompleting(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/sync-requests/${requestId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200"><RefreshCw className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-100 text-slate-700 border-slate-200"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'added':
        return <span className="text-emerald-600 font-medium">➕ Added</span>;
      case 'deleted':
        return <span className="text-red-600 font-medium">➖ Deleted</span>;
      case 'updated':
        return <span className="text-blue-600 font-medium">✏️ Updated</span>;
      default:
        return <span>{action}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 mx-auto text-indigo-500 mb-4 animate-spin" />
          <p className="text-slate-600">Loading sync requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sync-requests-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sync Requests
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin() ? 'Manage field option update requests from clients' : 'Track your field option update requests'}
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchRequests(); fetchStats(); fetchLocations(); }}
          data-testid="refresh-requests"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Location</span>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[260px]" data-testid="location-filter">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">From</span>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[160px]"
              data-testid="date-from"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">To</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[160px]"
              data-testid="date-to"
            />
          </div>

          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-slate-600"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div 
          className={`p-4 rounded-xl border cursor-pointer transition-all ${statusFilter === 'pending' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white hover:border-amber-200'}`}
          onClick={() => setStatusFilter('pending')}
          data-testid="stats-pending"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.pending}</p>
              <p className="text-sm text-slate-500">Pending</p>
            </div>
          </div>
        </div>
        
        <div 
          className={`p-4 rounded-xl border cursor-pointer transition-all ${statusFilter === 'in_progress' ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'}`}
          onClick={() => setStatusFilter('in_progress')}
          data-testid="stats-in-progress"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.in_progress}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </div>
        </div>
        
        <div 
          className={`p-4 rounded-xl border cursor-pointer transition-all ${statusFilter === 'completed' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-emerald-200'}`}
          onClick={() => setStatusFilter('completed')}
          data-testid="stats-completed"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.completed}</p>
              <p className="text-sm text-slate-500">Completed</p>
            </div>
          </div>
        </div>
        
        <div 
          className={`p-4 rounded-xl border cursor-pointer transition-all ${statusFilter === 'all' ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white hover:border-indigo-200'}`}
          onClick={() => setStatusFilter('all')}
          data-testid="stats-total"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Filter className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">All Requests</p>
            </div>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            <span className="ml-2 text-slate-600">Loading requests...</span>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
            <p className="text-slate-600">No {statusFilter !== 'all' ? statusFilter : ''} requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {requests.map((request) => (
              <div key={request.id} className="hover:bg-slate-50 transition-colors">
                {/* Request Header */}
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {expandedRequest === request.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{request.field_name}</span>
                          {request.type === 'USER_REMOVAL' && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">User Removal</Badge>
                          )}
                          {getStatusBadge(request.status)}
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                          <span className="font-medium">{request.location_name}</span>
                          <span className="mx-2">•</span>
                          <span>{request.type === 'USER_REMOVAL' ? 'Removal request' : `${request.changes?.length || 0} changes`}</span>
                          <span className="mx-2">•</span>
                          <span>by {request.requested_by_name}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-400">{formatDate(request.requested_at)}</span>
                      {isAdmin() && request.status === 'pending' && (
                        <Button 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleUpdateStatus(request.id, 'in_progress'); }}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`start-${request.id}`}
                        >
                          Start
                        </Button>
                      )}
                      {isAdmin() && request.status === 'in_progress' && (
                        <Button 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setCompleteDialog(request); }}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`complete-${request.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRequest === request.id && (
                  <div className="px-4 pb-4 ml-9">
                    <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                      {/* Changes Table */}
                      <div>
                        <h4 className="font-medium text-slate-700 mb-2">{request.type === 'USER_REMOVAL' ? 'Request Details:' : 'Requested Changes:'}</h4>
                        {request.type === 'USER_REMOVAL' ? (
                          <div className="text-sm text-slate-700 space-y-2">
                            <div>
                              <span className="text-slate-500">CRM User:</span>
                              <span className="ml-2 font-medium">{request.crm_user_name || '-'}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">CRM User ID:</span>
                              <span className="ml-2 font-mono text-xs">{request.crm_user_id || '-'}</span>
                            </div>
                          </div>
                        ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 text-slate-600 font-medium">Action</th>
                              <th className="text-left py-2 px-3 text-slate-600 font-medium">Option</th>
                              <th className="text-left py-2 px-3 text-slate-600 font-medium">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {request.changes?.map((change, i) => (
                              <tr key={i} className="border-b border-slate-100 last:border-0">
                                <td className="py-2 px-3">{getActionBadge(change.action)}</td>
                                <td className="py-2 px-3 font-medium">{change.option_name}</td>
                                <td className="py-2 px-3 text-slate-500">
                                  {change.action === 'updated' && (
                                    <span>{change.old_value} → {change.new_value}</span>
                                  )}
                                  {change.action === 'added' && (
                                    <span>New option added</span>
                                  )}
                                  {change.action === 'deleted' && (
                                    <span>Option removed</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        )}
                      </div>

                      {/* Meta Info */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Requested by:</span>
                          <span className="ml-2 text-slate-700">{request.requested_by_name} ({request.requested_by_email})</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Type:</span>
                          <span className="ml-2 text-slate-700">{request.type === 'USER_REMOVAL' ? 'User Removal' : (request.field_type || '-')}</span>
                        </div>
                        {request.completed_at && (
                          <>
                            <div>
                              <span className="text-slate-500">Completed by:</span>
                              <span className="ml-2 text-slate-700">{request.completed_by_name}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Completed at:</span>
                              <span className="ml-2 text-slate-700">{formatDate(request.completed_at)}</span>
                            </div>
                          </>
                        )}
                        {request.notes && (
                          <div className="col-span-2">
                            <span className="text-slate-500">Notes:</span>
                            <p className="ml-2 text-slate-700 mt-1">{request.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* CRM Link */}
                      {request.crm_link && (
                        <div className="pt-2 border-t border-slate-200">
                          <a 
                            href={request.crm_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
                            data-testid={`crm-link-${request.id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open in CRM
                            <ArrowUpRight className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Complete Dialog */}
      <Dialog open={!!completeDialog} onOpenChange={() => { setCompleteDialog(null); setCompletionNotes(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Complete Sync Request
            </DialogTitle>
            <DialogDescription>
              Mark this request as completed. The user will be notified via email.
            </DialogDescription>
          </DialogHeader>
          
          {completeDialog && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><span className="font-medium">Request:</span> {completeDialog.type === 'USER_REMOVAL' ? 'User Removal' : completeDialog.field_name}</p>
                <p><span className="font-medium">Location:</span> {completeDialog.location_name}</p>
                <p><span className="font-medium">Requested by:</span> {completeDialog.requested_by_name}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Completion Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes about the changes made..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={3}
                  data-testid="completion-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteDialog(null); setCompletionNotes(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkComplete}
              disabled={completing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-complete"
            >
              {completing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Completing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Complete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SyncRequestsPage;
